import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCompanies } from '../hooks/useApi';

/**
 * This component handles dynamic meta tag generation for company pages
 */
const CompanyMeta = () => {
  const { company } = useParams();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get all companies to find by name
  const { data: allCompanies, isLoading: allCompaniesLoading, error: companiesError } = useCompanies();
  
  // Extract company data when available
  useEffect(() => {
    if (!allCompaniesLoading && allCompanies) {
      try {
        // Safety check for invalid paths
        if (!company) {
          setError('Invalid company name');
          setIsLoading(false);
          return;
        }

        // Format the company name from URL for comparison
        const formattedUrlName = company.replace(/-/g, ' ').toLowerCase();
        
        // Find company by matching name
        const foundCompany = allCompanies.find(c => 
          c.name && c.name.toLowerCase() === formattedUrlName
        );

        if (foundCompany) {
          setCompanyData(foundCompany);
        } else {
          setError('Company not found');
        }
      } catch (err) {
        console.error('Error processing company data:', err);
        setError('Error processing company data');
      } finally {
        setIsLoading(false);
      }
    } else if (companiesError) {
      setError('Failed to load companies data');
      setIsLoading(false);
    }
  }, [allCompanies, allCompaniesLoading, company, companiesError]);

  // Helper function to create slugs
  const slugify = (str) => {
    if (!str) return '';
    return str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
  };

  // Generate proper meta description for sharing
  const generateMetaDescription = () => {
    if (!companyData) return 'Company details on Fresher Job Adda';
    
    return `${companyData.name} is hiring. View all jobs at ${companyData.name} on Fresher Job Adda. ${companyData.description?.substring(0, 100) || ''}`;
  };

  // Generate canonical URL
  const getCanonicalUrl = () => {
    if (!companyData) return window.location.href;
    
    const formattedName = slugify(companyData.name);
    return `${window.location.origin}/company/${formattedName}`;
  };

  // Get the company logo or default image
  const getCompanyLogo = () => {
    if (!companyData) return `${window.location.origin}/logo192.png`;
    
    if (companyData.image_url && companyData.image_url.trim() !== '') {
      return companyData.image_url;
    }
    
    return companyData.logo && companyData.logo.trim() !== '' 
      ? companyData.logo 
      : `${window.location.origin}/logo192.png`;
  };

  // Generate structured data
  const getStructuredData = () => {
    if (!companyData) return {};
    
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": companyData.name,
      "url": getCanonicalUrl(),
      "logo": getCompanyLogo(),
      "description": companyData.description || generateMetaDescription(),
      "sameAs": [
        companyData.website || window.location.origin,
        companyData.linkedin || '',
        companyData.twitter || ''
      ].filter(url => url)
    };
  };

  if (isLoading) {
    return (
      <Helmet>
        <title>Loading Company | Fresher Job Adda</title>
        <meta name="description" content="Loading company details" />
      </Helmet>
    );
  }

  if (error || !companyData) {
    return (
      <Helmet>
        <title>Company Not Found | Fresher Job Adda</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="The requested company could not be found" />
        <meta property="og:title" content="Company Not Found | Fresher Job Adda" />
        <meta property="og:description" content="The requested company could not be found" />
        <meta property="og:image" content={`${window.location.origin}/logo192.png`} />
        <meta name="twitter:title" content="Company Not Found | Fresher Job Adda" />
        <meta name="twitter:description" content="The requested company could not be found" />
        <meta name="twitter:image" content={`${window.location.origin}/logo192.png`} />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <title>{companyData.name} | Jobs & Career | Fresher Job Adda</title>
      
      {/* Basic meta tags */}
      <meta name="description" content={generateMetaDescription()} />
      <link rel="canonical" href={getCanonicalUrl()} />
      
      {/* Open Graph tags for Facebook, LinkedIn, WhatsApp */}
      <meta property="og:title" content={`${companyData.name} | Jobs & Career`} />
      <meta property="og:description" content={generateMetaDescription()} />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fresher Job Adda" />
      <meta property="og:image" content={getCompanyLogo()} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${companyData.name} | Jobs & Career`} />
      <meta name="twitter:description" content={generateMetaDescription()} />
      <meta name="twitter:image" content={getCompanyLogo()} />
      
      {/* Additional company-specific metadata */}
      <meta name="keywords" content={`jobs, career, ${companyData.name}, hiring, employment opportunities, ${companyData.industry || ''}`} />
      <meta name="robots" content="index, follow" />
      
      {/* Company specific meta tags */}
      <meta name="company:name" content={companyData.name} />
      <meta name="company:industry" content={companyData.industry || ''} />
      <meta name="company:location" content={companyData.location || ''} />
      <meta name="company:website" content={companyData.website || ''} />
      <meta name="company:image" content={getCompanyLogo()} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};

export default CompanyMeta; 